<script lang="ts">
	import { Button, Col, Icon, Input, Row, Stack, Tooltip } from '@Shelly/shelly-design-system/index';
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';

	const dispatch = createEventDispatcher();

	export let settings: any = {};

	let snr = settings.presence?.sensor?.snr ?? 50;
	let points = settings.presence?.sensor?.points ?? 40;
	let velocity = settings.presence?.sensor?.velocity ?? 0.01;
	let maxVelocity = settings.presence?.sensor?.max_velocity ?? 20;
	let detActThr = settings.presence?.sensor?.state?.det_act_thr ?? 3;
	let detFreeThr = settings.presence?.sensor?.state?.det_free_thr ?? 3;
	let actFreeThr = settings.presence?.sensor?.state?.act_free_thr ?? 12;
	let statFreeThr = settings.presence?.sensor?.state?.stat_free_thr ?? 50;
	let sleepFreeThr = settings.presence?.sensor?.state?.sleep_free_thr ?? 100;

	function handleDiscard() {
		snr = settings.presence?.sensor?.snr ?? 50;
		points = settings.presence?.sensor?.points ?? 40;
		velocity = settings.presence?.sensor?.velocity ?? 0.01;
		maxVelocity = settings.presence?.sensor?.max_velocity ?? 20;
		detActThr = settings.presence?.sensor?.state?.det_act_thr ?? 3;
		detFreeThr = settings.presence?.sensor?.state?.det_free_thr ?? 3;
		actFreeThr = settings.presence?.sensor?.state?.act_free_thr ?? 12;
		statFreeThr = settings.presence?.sensor?.state?.stat_free_thr ?? 50;
		sleepFreeThr = settings.presence?.sensor?.state?.sleep_free_thr ?? 100;
	}

	function handleSave() {
		dispatch('configSave', {
			sensor: {
				snr: Number(snr),
				points: Number(points),
				velocity: Number(velocity),
				max_velocity: Number(maxVelocity),
				state: {
					det_act_thr: Number(detActThr),
					det_free_thr: Number(detFreeThr),
					act_free_thr: Number(actFreeThr),
					stat_free_thr: Number(statFreeThr),
					sleep_free_thr: Number(sleepFreeThr),
				},
			},
		});
	}
</script>

<Stack gap="s" class="mt-s">
	<Row>
		<Col>
			<div class="info-banner">
				<Icon icon="fas fa-circle-info" size="s" class="info-icon" />
				<span
					>{$format('presence_fine_tuning_frame_info', {
						default: '1 frame = 100 ms (10 frames per second)',
					})}</span
				>
			</div>
		</Col>
	</Row>

	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_fine_tuning_snr_label', { default: 'SNR threshold' })}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_fine_tuning_snr_tooltip', {
						default:
							'Set the minimum signal strength to detect motion. Lower values increase sensitivity; higher values reduce false detections. Range: 10 to 100.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Input
				type="number"
				min="10"
				max="100"
				step="1"
				bind:value={snr}
				label={$format('presence_fine_tuning_snr_label', { default: 'SNR threshold' })}
				id="input-snr"
				aria-labelledby="input-snr"
			/>
		</Col>
	</Row>
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_fine_tuning_points_label', {
						default: 'Object recognition threshold',
					})}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_fine_tuning_points_tooltip', {
						default:
							'Set the points required to recognize an object. Lower values allow detection of smaller or partially visible objects. Range: 10 to 100 points.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Input
				type="number"
				min="10"
				max="100"
				step="1"
				bind:value={points}
				label={$format('presence_fine_tuning_points_label', {
					default: 'Object recognition threshold',
				})}
				id="input-points"
				aria-labelledby="input-points"
			/>
		</Col>
	</Row>
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_fine_tuning_velocity_label', { default: 'Velocity threshold' })}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_fine_tuning_velocity_tooltip', {
						default:
							'Set the minimum speed to detect an object. Use low values to better detect slow movement. Use high values to ignore slow or stationary objects. Range: 0.01 m to 1 m per second.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Input
				type="number"
				min="0.01"
				max="1"
				step="0.01"
				bind:value={velocity}
				label={$format('presence_fine_tuning_velocity_label', { default: 'Velocity threshold' })}
				id="input-velocity"
				aria-labelledby="input-velocity"
			/>
		</Col>
	</Row>
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_fine_tuning_max_velocity_label', {
						default: 'Max velocity difference',
					})}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_fine_tuning_max_velocity_tooltip', {
						default:
							'Set the speed variance for grouping points as one target. Prevents unrelated motion (e.g., one object moving past another) from being grouped together. Range: 1 m to 50 m per second.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Input
				type="number"
				min="1"
				max="50"
				step="1"
				bind:value={maxVelocity}
				label={$format('presence_fine_tuning_max_velocity_label', {
					default: 'Max velocity difference',
				})}
				id="input-max-velocity"
				aria-labelledby="input-max-velocity"
			/>
		</Col>
	</Row>
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_fine_tuning_det_act_label', {
						default: 'Motion activation threshold (det_act_thr)',
					})}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_fine_tuning_det_act_tooltip', {
						default:
							'Set the frames required to mark an object as active. Lower values react faster to motion; higher values improve stability and reduce false triggers. Range: 1 to 100 frames.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Input
				type="number"
				min="1"
				max="100"
				step="1"
				bind:value={detActThr}
				label={$format('presence_fine_tuning_det_act_label', {
					default: 'Motion activation threshold (det_act_thr)',
				})}
				id="input-det-act-thr"
				aria-labelledby="input-det-act-thr"
			/>
		</Col>
	</Row>
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_fine_tuning_det_free_label', {
						default: 'Motion release threshold (det_free_thr)',
					})}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_fine_tuning_det_free_tooltip', {
						default:
							'Set the frames required to clear a detection. Lower values stop detection quickly; higher values help avoid flickering. Range: 1 to 100 frames.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Input
				type="number"
				min="1"
				max="100"
				step="1"
				bind:value={detFreeThr}
				label={$format('presence_fine_tuning_det_free_label', {
					default: 'Motion release threshold (det_free_thr)',
				})}
				id="input-det-free-thr"
				aria-labelledby="input-det-free-thr"
			/>
		</Col>
	</Row>
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_fine_tuning_act_free_label', {
						default: 'Tracking loss threshold (act_free_thr)',
					})}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_fine_tuning_act_free_tooltip', {
						default:
							'Set the frames required for an active object to switch to free (not shown on the map). Lower values switch sooner; higher values delay the transition. Range: 1 to 1000 frames.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Input
				type="number"
				min="1"
				max="1000"
				step="1"
				bind:value={actFreeThr}
				label={$format('presence_fine_tuning_act_free_label', {
					default: 'Tracking loss threshold (act_free_thr)',
				})}
				id="input-act-free-thr"
				aria-labelledby="input-act-free-thr"
			/>
		</Col>
	</Row>
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_fine_tuning_stat_free_label', {
						default: 'Stillness tracking threshold (stat_free_thr)',
					})}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_fine_tuning_stat_free_tooltip', {
						default:
							'Set the frames required to confirm a stationary object as presence. Higher values improve the detection of stationary objects. Range: 1 to 1000 frames.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Input
				type="number"
				min="1"
				max="1000"
				step="1"
				bind:value={statFreeThr}
				label={$format('presence_fine_tuning_stat_free_label', {
					default: 'Stillness tracking threshold (stat_free_thr)',
				})}
				id="input-stat-free-thr"
				aria-labelledby="input-stat-free-thr"
			/>
		</Col>
	</Row>
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_fine_tuning_sleep_free_label', {
						default: 'Stillness timeout threshold (sleep_free_thr)',
					})}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_fine_tuning_sleep_free_tooltip', {
						default:
							'Set the maximum time to hold a detection when no motion is sensed. Higher values maintain presence during long inactivity of stationary objects. Range: 1 to 65535 frames.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Input
				type="number"
				min="1"
				max="65535"
				step="1"
				bind:value={sleepFreeThr}
				label={$format('presence_fine_tuning_sleep_free_label', {
					default: 'Stillness timeout threshold (sleep_free_thr)',
				})}
				id="input-sleep-free-thr"
				aria-labelledby="input-sleep-free-thr"
			/>
		</Col>
	</Row>

	<hr class="mt-l mb-s" />
	<Row>
		<Col>
			<div class="config-actions">
				<Button variant="secondary" size="m" on:click={handleDiscard}>
					{$format('button_discard_changes', { default: 'Discard changes' })}
				</Button>
				<Button variant="primary" size="m" on:click={handleSave}>
					{$format('button_save_settings', { default: 'Save settings' })}
				</Button>
			</div>
		</Col>
	</Row>
</Stack>

<style>
	.info-banner {
		display: flex;
		align-items: center;
		gap: 8px;
		background: var(--color-info-subtle, rgba(0, 136, 255, 0.12));
		border: 1px solid var(--color-info, #0088ff);
		border-radius: 8px;
		padding: 10px 14px;
		font-size: 13px;
		color: var(--color-text-primary);
	}

	.config-actions {
		display: flex;
		gap: 8px;
		justify-content: flex-end;
	}

	.headline-with-info {
		display: flex;
		align-content: space-between;
		align-items: baseline;
		gap: 4px;
		margin-bottom: 0.5rem;
	}

	.headline-with-info p {
		margin: 0;
	}
</style>
